/**
 * Main layout component with collapsible sidebar (W1 S0b extraction from
 * App.tsx, moved verbatim).
 *
 * W1-T17 (Stream 7 shell closure) mounts the shell primitives that S1-S5
 * built but left deliberately unmounted, pending this task (see each
 * component's own "Deliberately NOT mounted... handoff to Stream 7"
 * doc comment: ScopeRibbon.tsx, HelpDrawer.tsx, JobsDrawer.tsx,
 * ApprovalsPanel.tsx, AlertsDrawer.tsx). All are self-hiding when the
 * w1Shell flag is off or their own open condition isn't met, so mounting
 * them unconditionally here does not change flag-OFF behavior:
 *  - ScopeRibbon (G01) — always rendered when the flag is on; renders
 *    "All environments" itself when the URL carries no scope (see its own
 *    doc comment), so it is not further route-gated here.
 *  - Breadcrumb (G02) — only for the entity/work routes breadcrumb.ts
 *    resolves (the three pages that already hand-roll their own are
 *    excluded there, so nothing double-renders).
 *  - JobsDrawer/ApprovalsDrawer/AlertsDrawer (G04/G05/G06) — URL-driven;
 *    `null` unless their own query param is present.
 *  - HelpDrawer (G07) — the one drawer NOT URL-driven (its own doc
 *    comment: "the caller... owns an `open` boolean"); that open boolean
 *    is owned here so it survives Header's own re-renders and the trigger
 *    button (Header) and the drawer (Layout) share one source of truth.
 *
 * Story #5343 (2026-09-02 design-match audit) adds the four shared-chrome
 * components here — ScreenHeader, DomainTabs, FocusBar and ScreenFooter, via
 * ScreenChromeTop/Bottom. They were missing 317 times across the 143 approved
 * screens; mounting them once in the shell fixes all of them without editing a
 * single page component, because they read the route's row in the approved
 * screen contract rather than per-page props.
 *
 * ShellChromeProvider then tells page-level copies of the same chrome to stand
 * down: 24 pages render their own ScopeRibbon, 18 their own HelpDrawer, and 19
 * a per-domain tab strip. Without it every one of those screens would show two.
 * The shell's own instances pass `shell` so they always render.
 */
import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { STORAGE_KEYS } from '../../constants/config';
import Sidebar from './Sidebar';
import Header from './Header';
import { ScopeRibbon } from '../scope/ScopeRibbon';
import Breadcrumb from '../ui/Breadcrumb';
import JobsDrawer from '../jobs/JobsDrawer';
import ApprovalsDrawer from '../approvals/ApprovalsDrawer';
import AlertsDrawer from '../alerts/AlertsDrawer';
import HelpDrawer from '../help/HelpDrawer';
import { isW1Shell } from '../../utils/w1Flag';
import { resolveShellBreadcrumb } from './breadcrumb';
import { ScreenChromeTop, ScreenChromeBottom } from '../../chrome/ScreenChrome';
import { ShellChromeProvider } from '../../chrome/ShellChromeContext';
import { useScreenContract } from '../../chrome/useScreenContract';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED);
    return saved === 'true';
  });

  const toggleCollapse = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(newValue));
      return newValue;
    });
  }, []);

  const flagOn = isW1Shell();
  const screen = useScreenContract();
  /**
   * Gated behind VITE_SHELL_CHROME. ON since 2026-09-03, second design.
   *
   * The first design shipped correct in isolation and wrong in place. Every
   * test rendered the shell around a STUB page -- the one shape with no header
   * of its own -- so nothing could see that 99 of the 122 covered screens
   * already draw their own <h1> (and 23 their own breadcrumb). Live, nearly
   * every screen showed its title, crumb and description twice, and Rob called
   * the portal unintelligible. Three further defects the same screenshots
   * showed: tabs whose route needs an id the URL does not carry rendered as
   * inert grey text (E03 had fourteen); Focus/Owner/Scope-evidence printed the
   * contract's full prose truncated to ~40 characters, so all three read as
   * "Approvals visible to the signed-in sub..."; and Home drew its whole menu
   * twice, because its tabs are role="tablist" buttons and its crumb is the
   * shared <Breadcrumb>, neither of which the first duplicate-header gate
   * looked for.
   *
   * All four are fixed and each has a test that fails for the original reason:
   *   - noDuplicateHeaders.test.ts reads App.tsx, maps every chromed route to
   *     its file, and asserts no page-title <h1>, no own tab strip or crumb
   *     that does not stand down, and no hand-rolled text breadcrumb;
   *   - shellComposition.test.tsx renders the shell around REAL page
   *     components, not stubs, and counts the <h1>s an operator would see.
   *
   * The design is option B, which Rob chose on 2026-09-03 from three rendered
   * alternatives: one header line, ownership on a second line with the rest
   * behind a disclosure, then the tabs. See ScreenChrome.tsx.
   */
  const chromeOn =
    flagOn && screen !== null && import.meta.env.VITE_SHELL_CHROME === 'true';
  const breadcrumbItems = flagOn && !chromeOn ? resolveShellBreadcrumb(location.pathname) : null;

  const shellChrome = {
    scopeRibbon: flagOn,
    help: flagOn,
    tabs: chromeOn,
    header: chromeOn,
  };

  return (
    <ShellChromeProvider value={shellChrome}>
    <div className="min-h-screen bg-iflow-50 flex">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={toggleCollapse}
        onOpenHelp={() => setHelpOpen(true)}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <Header onMenuClick={() => setSidebarOpen(true)} onOpenHelp={() => setHelpOpen(true)} />
        {flagOn && <ScopeRibbon shell />}
        <main className="flex-1 p-4 lg:p-6">
          {breadcrumbItems && breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}
          {chromeOn && <ScreenChromeTop onOpenHelp={() => setHelpOpen(true)} />}
          {children}
          {chromeOn && <ScreenChromeBottom />}
        </main>
      </div>

      {flagOn && (
        <>
          <JobsDrawer />
          <ApprovalsDrawer />
          <AlertsDrawer />
          <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} shell />
        </>
      )}
    </div>
    </ShellChromeProvider>
  );
};

export default Layout;
